import { useEffect, useState } from "react";
import { Upload, FileText, Plus, Trash2, Wand2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const SOURCE_OPTIONS = [
  { v: "", label: "Manual (blank)" },
  { v: "transaction.property_address", label: "Property address" },
  { v: "transaction.purchase_price", label: "Purchase price" },
  { v: "transaction.acceptance_date", label: "Acceptance date" },
  { v: "transaction.close_of_escrow_date", label: "Close of escrow date" },
  { v: "transaction.earnest_money", label: "Earnest money" },
  { v: "transaction.lender_name", label: "Lender" },
  { v: "transaction.title_company", label: "Title company" },
  { v: "transaction.escrow_officer", label: "Escrow officer" },
  { v: "agent.full_name", label: "Agent name" },
  { v: "agent.email", label: "Agent email" },
  { v: "agent.phone", label: "Agent phone" },
];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [txs, setTxs] = useState([]);
  const [txForFill, setTxForFill] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignTx, setAssignTx] = useState("");
  const [assigning, setAssigning] = useState(false);

  const assignToTx = async () => {
    if (!assignTx || !selected) return;
    setAssigning(true);
    try {
      const { data } = await api.post(`/form-templates/${selected.id}/assign`, { transaction_id: assignTx });
      toast.success(data.reused ? "Already assigned — reused existing document" : "Assigned · client can now see it in their portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assign failed");
    } finally { setAssigning(false); }
  };

  const load = async () => {
    const { data } = await api.get("/form-templates");
    setTemplates(data);
  };
  useEffect(() => { load(); api.get("/transactions").then(r => setTxs(r.data)); }, []);

  const openTemplate = async (id) => {
    const { data } = await api.get(`/form-templates/${id}`);
    setSelected(data);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !name) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", file);
    try {
      const { data } = await api.post("/form-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.is_acroform ? `Detected ${data.detected_fields} form fields` : "Uploaded (no AcroForm — will save as-is)");
      setName(""); setFile(null); load();
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const saveFields = async () => {
    try {
      await api.patch(`/form-templates/${selected.id}`, { fields: selected.fields });
      toast.success("Field mapping saved");
    } catch { toast.error("Save failed"); }
  };

  const fill = async () => {
    if (!txForFill) return toast.error("Pick a transaction first");
    try {
      const { data } = await api.post(`/form-templates/${selected.id}/fill`, { transaction_id: txForFill });
      toast.success(data.was_acroform ? "Filled PDF ready" : "Blank PDF stored — AcroForm not detected");
      window.open(data.download_url, "_blank");
    } catch (err) { toast.error(err?.response?.data?.detail || "Fill failed"); }
  };

  const updateField = (i, k, v) => {
    const next = [...selected.fields];
    next[i] = { ...next[i], [k]: v };
    setSelected({ ...selected, fields: next });
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Form templates</h1>
        <p className="mt-1 text-sm text-slate-500">Upload a blank PDF, map its fields to transaction data, then fill it in seconds. Uploading a pre-filled PDF is always available — use /documents.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <form onSubmit={upload} className="rounded-lg border border-slate-200 bg-white p-5 mb-6">
            <h2 className="font-heading font-bold text-sm tracking-tight text-slate-900">Upload new template</h2>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Template name (e.g. Buyer Broker Employment Agreement)" className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="tpl-name-input" />
            <div className="mt-3">
              <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="tpl-file-input" />
              <p className="text-xs text-slate-500 mt-2">PDFs with AcroForm fields auto-populate. Others save as-is and can still be tracked.</p>
            </div>
            <button type="submit" disabled={busy || !file || !name} className="mt-3 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="tpl-upload-btn">
              <Upload className="h-4 w-4" /> Upload template
            </button>
          </form>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading font-bold text-sm tracking-tight text-slate-900">Your templates</h2>
              <span className="text-xs text-slate-500">{templates.length}</span>
            </div>
            {templates.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="tpl-empty">No templates yet. Upload a PDF to get started.</div>}
            {templates.map(t => (
              <button key={t.id} onClick={() => openTemplate(t.id)} className={`w-full text-left px-5 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${selected?.id === t.id ? "bg-slate-50" : ""}`} data-testid={`tpl-row-${t.id}`}>
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                    </div>
                    <div className="text-xs text-slate-500">{(t.fields || []).length} field(s) {t.is_acroform ? "· AcroForm" : "· no AcroForm"}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          {!selected && <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">Pick a template to map fields.</div>}
          {selected && (
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">{selected.name}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{selected.is_acroform ? `AcroForm · ${selected.fields.length} fields` : "No AcroForm detected"}</p>
                </div>
              </div>
              {selected.fields.length === 0 && (
                <p className="mt-4 text-sm text-slate-500">No fields to map. Add one below to track manually.</p>
              )}
              <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                {selected.fields.map((f, i) => (
                  <div key={f.key || `field-${i}`} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`tpl-field-${i}`}>
                    <input value={f.key} onChange={e => updateField(i, "key", e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-xs font-mono" placeholder="pdf field key" />
                    <input value={f.label} onChange={e => updateField(i, "label", e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-xs" placeholder="label" />
                    <select value={f.source || ""} onChange={e => updateField(i, "source", e.target.value)} className="rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`tpl-source-${i}`}>
                      {SOURCE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                    <button onClick={() => setSelected({ ...selected, fields: selected.fields.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => setSelected({ ...selected, fields: [...selected.fields, { key: "", label: "", source: "", type: "text" }] })} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900" data-testid="tpl-add-field"><Plus className="h-3 w-3" /> Add field</button>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button onClick={saveFields} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="tpl-save-fields">Save mapping</button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-heading font-bold text-sm tracking-tight text-slate-900">Assign to transaction</h3>
                <p className="text-xs text-slate-500 mt-0.5">Attach this blank template to a transaction so the client can view + download it from their portal 'My Documents'.</p>
                <div className="mt-2 flex items-center gap-2">
                  <select value={assignTx} onChange={e => setAssignTx(e.target.value)} className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="tpl-assign-tx-select">
                    <option value="">Choose a transaction…</option>
                    {txs.map(t => <option key={t.id} value={t.id}>{t.property_address || "(no address)"} · {t.side}</option>)}
                  </select>
                  <button onClick={assignToTx} disabled={!assignTx || assigning} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="tpl-assign-btn">
                    <Link2 className="h-4 w-4" /> {assigning ? "Assigning…" : "Assign"}
                  </button>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-heading font-bold text-sm tracking-tight text-slate-900">Fill for transaction</h3>
                <div className="mt-2 flex items-center gap-2">
                  <select value={txForFill} onChange={e => setTxForFill(e.target.value)} className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="tpl-fill-tx-select">
                    <option value="">Choose a transaction…</option>
                    {txs.map(t => <option key={t.id} value={t.id}>{t.property_address || "(no address)"} · {t.side}</option>)}
                  </select>
                  <button onClick={fill} disabled={!txForFill} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="tpl-fill-btn">
                    <Wand2 className="h-4 w-4" /> Fill & download
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
